/**
 * ui-renderers/proactive.ts
 * Proactive suggestion cards, recent-docs popup, doc chip, and document suggestion pills.
 */

import { state } from '../state';
import { dom } from '../dom';
import { escapeHtml, escapeAttr } from './core';

export function showDocChip(name: string) {
  const chip     = dom.docChip;
  const chipName = dom.docChipName;
  const btn      = dom.attachBtn;
  if (chipName) chipName.textContent = name;
  if (chip)     chip.classList.add('visible');
  if (btn)      btn.classList.add('has-doc');
}

export function clearDocChip() {
  const chip = dom.docChip;
  const btn  = dom.attachBtn;
  if (chip) chip.classList.remove('visible');
  if (btn)  btn.classList.remove('has-doc');
}

export function renderProactiveSuggestions(suggestions: any[]) {
  const panel = dom.proactivePanel;
  const cards = dom.psCards;
  const pill  = dom.psPill;
  if (!panel || !cards) return;

  const visible = suggestions.filter(s => !state.psDismissed[s.id]);
  state.psCount = visible.length;

  if (!visible.length) {
    panel.classList.remove('has-suggestions');
    if (pill) pill.classList.remove('visible');
    return;
  }

  cards.innerHTML = visible.map(s => {
    const urgencyCls    = 'urgency-' + (s.urgency || 'low');
    const predictiveCls = s.source === 'predictive' ? ' ps-card-predictive' : '';
    const predictiveTag = s.source === 'predictive'
      ? '<span class="ps-predictive-chip" aria-label="Predicted by Kili">✨ Predicted</span>'
      : '';
    const label      = escapeHtml(s.button_label || 'Take action →');
    const actionAttr = s.action ? escapeAttr(JSON.stringify(s.action)) : '';
    return [
      '<div class="ps-card ' + urgencyCls + predictiveCls + '" data-ps-id="' + escapeAttr(s.id) + '">',
        '<div class="ps-card-header">',
          predictiveTag,
          '<div class="ps-card-title">' + escapeHtml(s.title) + '</div>',
        '</div>',
        '<div class="ps-card-desc">' + escapeHtml(s.description) + '</div>',
        '<div class="ps-card-footer">',
          s.action
            ? '<button class="ps-action-btn" data-action="' + actionAttr + '" data-id="' + escapeAttr(s.id) + '">' + label + '</button>'
            : '',
          '<button class="ps-dismiss-btn" data-dismiss-id="' + escapeAttr(s.id) + '" aria-label="Dismiss">✕</button>',
        '</div>',
      '</div>',
    ].join('');
  }).join('');

  if (state.psCollapsed) {
    panel.classList.remove('has-suggestions');
    if (pill) pill.classList.add('visible');
  } else {
    panel.classList.add('has-suggestions');
    if (pill) pill.classList.remove('visible');
  }

  cards.querySelectorAll('.ps-action-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      const id     = (btn as HTMLElement).dataset.id;
      const action = (btn as HTMLElement).dataset.action
        ? JSON.parse((btn as HTMLElement).dataset.action!)
        : null;
      document.dispatchEvent(new CustomEvent('akili:ps-action', { detail: { id, action } }));
    });
  });

  cards.querySelectorAll('.ps-dismiss-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      document.dispatchEvent(new CustomEvent('akili:ps-dismiss', { detail: (btn as HTMLElement).dataset.dismissId }));
    });
  });
}

export function showRecentDocsGrid() {
  const existing = document.querySelector('.ai-recent-docs-popup');
  if (existing && existing.parentNode) { existing.parentNode.removeChild(existing); return; }

  const popup = document.createElement('div');
  popup.className = 'ai-recent-docs-popup';

  const title = document.createElement('div');
  title.className = 'ai-recent-docs-title';
  title.textContent = 'Recent Documents';
  popup.appendChild(title);

  if (!state.recentDocs.length) {
    const empty = document.createElement('div');
    empty.className = 'ai-recent-docs-empty';
    empty.textContent = 'No recent documents. Attach one from the matter page.';
    popup.appendChild(empty);
  } else {
    state.recentDocs.forEach(doc => {
      const item = document.createElement('div');
      item.className = 'ai-recent-doc-item';
      item.textContent = doc.name || doc.id;
      item.title = doc.name || doc.id;
      item.addEventListener('click', () => {
        document.dispatchEvent(new CustomEvent('akili:attach-doc', {
          detail: { id: doc.id, name: doc.name },
        }));
        if (popup.parentNode) popup.parentNode.removeChild(popup);
      });
      popup.appendChild(item);
    });
  }

  document.body.appendChild(popup);
  setTimeout(() => {
    const dismiss = (e: MouseEvent) => {
      if (!popup.contains(e.target as Node)) {
        if (popup.parentNode) popup.parentNode.removeChild(popup);
      }
    };
    document.addEventListener('click', dismiss, { once: true });
  }, 50);
}

export function renderDocumentSuggestions(
  suggestions: Array<{ id: string; title: string; url?: string }>,
  bubbleEl: HTMLElement,
) {
  if (!suggestions || !suggestions.length || !bubbleEl) return;

  const existing = bubbleEl.querySelector('.ai-doc-suggestions');
  if (existing && existing.parentNode) existing.parentNode.removeChild(existing);

  const row = document.createElement('div');
  row.className = 'ai-doc-suggestions';
  row.setAttribute('aria-label', 'Suggested documents');

  const label = document.createElement('span');
  label.className = 'ai-doc-suggestions-label';
  label.textContent = '💡 Related documents';
  row.appendChild(label);

  const pills = document.createElement('div');
  pills.className = 'ai-doc-suggestions-pills';

  suggestions.forEach(doc => {
    const pill = document.createElement('a');
    pill.className = 'ai-doc-pill';
    pill.textContent = doc.title || doc.id || 'Document';
    pill.title = doc.title || 'Open document';
    pill.setAttribute('role', 'button');
    pill.setAttribute('tabindex', '0');

    const href = doc.url || ('/documents/' + encodeURIComponent(doc.id || ''));
    pill.href = href;
    pill.target = '_blank';
    pill.rel = 'noopener';

    pill.addEventListener('click', () => {
      document.dispatchEvent(new CustomEvent('akili:open-document', {
        detail: { id: doc.id, title: doc.title, url: href },
      }));
    });

    pill.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); pill.click(); }
    });

    pills.appendChild(pill);
  });

  row.appendChild(pills);

  const bodyWrap = bubbleEl.querySelector('.ai-msg-body-wrap');
  if (bodyWrap && bodyWrap.nextSibling) {
    bubbleEl.insertBefore(row, bodyWrap.nextSibling);
  } else {
    bubbleEl.appendChild(row);
  }

  const msgsEl = dom.msgs;
  if (msgsEl) requestAnimationFrame(() => { msgsEl.scrollTop = msgsEl.scrollHeight; });
}
